import { FUNKIT_CONNECT_SUPPORTED_CHAINS_INFO_LIST } from '@funkit/chains'
import {
  type RelayQuoteResult,
  type RelayVmType,
  executeRelayQuote,
  initializeRelayClient,
} from '@funkit/fun-relay'
import type { Hex } from 'viem'
import type {
  WithdrawalClient,
  WithdrawalTypedData,
} from '../interfaces/withdrawalClient'
import type { FunLogger } from '../utils/funLogger'
import { isUserRejectedError } from '../utils/signature'

// Provider-agnostic withdrawal plumbing shared by every withdrawal path (Swapped
// fiat off-ramp today, native crypto send-to-address later): relay-client init,
// source-chain switching, the relay wallet adapter, and relay execution.
// Provider-specific routing / order handling lives in the per-provider domain
// (e.g. ./swappedWithdrawal), which consumes these.

let relayClientReady = false

/**
 * Idempotent, lazy init of the process-global relay client over the funkit
 * chain list (mirrors web's `FunkitProvider` init). Callers invoke it right
 * before the first execution so flows that never withdraw don't touch relay.
 *
 * `source` MUST be passed on React Native: relay-sdk auto-derives one from
 * `location.hostname` when omitted, gated only on `typeof window !== 'undefined'`
 * — and RN's Hermes runtime has a `window` but no `location`, so the auto-derive
 * throws `Property 'location' doesn't exist`. An explicit source skips it.
 */
export function ensureRelayClientInitialized(
  logger: FunLogger,
  source = 'funkit-connect',
): void {
  if (relayClientReady) {
    return
  }
  initializeRelayClient({
    // Already the projected viem Chain list (`.chainInfo` of each entry).
    chains: FUNKIT_CONNECT_SUPPORTED_CHAINS_INFO_LIST,
    logger,
    source,
  })
  relayClientReady = true
}

/** Switch the wallet to the source chain if it isn't already there. */
export async function ensureOnSourceChain(
  wallet: WithdrawalClient,
  sourceChainId: number,
  logger: FunLogger,
): Promise<void> {
  const currentChainId = await wallet.getChainId()
  if (currentChainId === sourceChainId) {
    return
  }
  logger.info('withdrawal:switching_chain', {
    from: currentChainId,
    to: sourceChainId,
  })
  await wallet.switchChain(sourceChainId)
}

/**
 * Adapt the host {@link WithdrawalClient} into the evm-shaped relay wallet
 * `executeRelayQuote` drives — i.e. WithdrawalClient-based EVM relay execution.
 *
 * Faithful port of ONE branch of web's inline adapter in
 * `useCheckoutDirectExecution` — the `withdrawalClient ? {...}` EVM branch. It
 * deliberately does NOT cover web's other two branches (raw-viem checkout
 * deposits, non-EVM Lighter) nor web-only preWithdrawalAction tx prepending —
 * none are on the core surface today.
 */
export function toRelayWallet(wallet: WithdrawalClient, logger: FunLogger) {
  return {
    vmType: 'evm' as const,
    getChainId: () => wallet.getChainId(),
    handleSignMessageStep: async (stepItem: {
      data?: {
        sign?: {
          signatureKind?: string
          message?: string
          domain?: WithdrawalTypedData['domain']
          types?: WithdrawalTypedData['types']
          primaryType?: string
          value?: WithdrawalTypedData['message']
        }
      }
    }): Promise<string> => {
      const sign = stepItem.data?.sign
      const context = { signatureKind: sign?.signatureKind }

      if (sign?.signatureKind === 'eip712' && wallet.signTypedData) {
        // Relay types `value` as optional on every sign step, so an eip712 step
        // without it must fail here rather than reach the wallet as
        // `message: undefined`.
        if (!sign.value || !sign.domain || !sign.types || !sign.primaryType) {
          logger.error(
            'withdrawal:malformedSignatureStep',
            new Error('eip712 signature step is missing its typed data'),
            context,
          )
          throw new Error('eip712 signature step is missing its typed data')
        }
        // Transaction-path decision: a gasless signature replaces the approve
        // and transfer transactions.
        logger.info('withdrawal:gaslessSignatureStep', context)
        return wallet.signTypedData({
          domain: sign.domain,
          types: sign.types,
          primaryType: sign.primaryType,
          message: sign.value,
        })
      }

      if (sign?.signatureKind === 'eip191' && wallet.signMessage) {
        // `message` is optional on every sign step, so a step without one is
        // malformed — not a client that can't sign.
        if (sign.message == null) {
          logger.error(
            'withdrawal:malformedSignatureStep',
            new Error('eip191 signature step is missing its message'),
            context,
          )
          throw new Error('eip191 signature step is missing its message')
        }
        logger.info('withdrawal:gaslessSignatureStep', context)
        // A hash IS the whole message, so this is anchored where relay-kit's
        // own adapter is not: text that merely contains 32 hex bytes must be
        // signed as text.
        return /^0x[0-9a-fA-F]{64}$/.test(sign.message)
          ? wallet.signMessage({ raw: sign.message })
          : wallet.signMessage(sign.message)
      }

      // Unrecoverable: a gasless quote reached a client that can't sign it, so
      // the withdrawal aborts. Alertable — the quote and the client disagree
      // about which transaction path is supported.
      logger.error(
        'withdrawal:missingWithdrawalSigner',
        new Error('withdrawal client cannot sign this signature step'),
        {
          ...context,
          hasSignTypedData: Boolean(wallet.signTypedData),
          hasSignMessage: Boolean(wallet.signMessage),
        },
      )
      throw new Error(
        `withdrawal client cannot sign ${sign?.signatureKind ?? 'unknown'} signature steps`,
      )
    },
    handleSendTransactionStep: async (
      chainId: number,
      step: { data: { to: string; value: string; data: string } },
    ) => {
      const stepData = step.data
      return wallet.sendTransaction(chainId, {
        to: stepData.to,
        value: stepData.value,
        data: stepData.data,
      })
    },
    handleConfirmTransactionStep: async (txHash: string, chainId: number) => {
      return wallet.confirmTransaction(txHash, chainId)
    },
    switchChain: async (): Promise<void> => {
      throw new Error('unsupported operation in withdrawal')
    },
    address: async () => wallet.address(),
    // Opt-in atomic batching: hosts that supply `sendTransactions` get
    // approve + swap collapsed into one user signature via relay's batched
    // step; otherwise relay falls back to sequential single transactions.
    ...(wallet.sendTransactions
      ? {
          supportsAtomicBatch: async () => true,
          handleBatchTransactionStep: async (
            chainId: number,
            items: { data: { to: string; value: string; data: string } }[],
          ) => {
            if (!wallet.sendTransactions) {
              // Only reachable if the host mutates the wallet object after the
              // surrounding spread captured it — log for visibility.
              logger.error(
                'withdrawal:sendTransactionsMissing',
                new Error('sendTransactions is not supported'),
                { chainId, itemsCount: items.length },
              )
              throw new Error('sendTransactions is not supported')
            }
            return wallet.sendTransactions(
              chainId,
              items.map((item) => ({
                to: item.data.to,
                value: item.data.value,
                data: item.data.data,
              })),
            )
          },
        }
      : {}),
  }
}

/** Log namespace, one per withdrawal rail. */
export type WithdrawalRail = 'crypto-withdrawal' | 'fiat-withdrawal'

/** The id a rail correlates a withdrawal by, across its logs. */
export type WithdrawalLogContext =
  | { submission_id: string }
  | { order_id: string }

/**
 * Resolve once the user has done everything relay asked of them, with the hash
 * relay reported — on-chain confirmation is driven separately by the wallet's
 * own `confirmTransaction` inside the relay steps.
 *
 * `undefined` means relay reported neither a hash nor an error; both rails treat
 * that as unrecoverable, since funds may already have moved.
 */
export async function executeWithdrawalRelayQuote({
  relayQuote,
  wallet,
  logger,
  rail,
  context,
  onStepMessage,
}: {
  relayQuote: RelayQuoteResult<RelayVmType>
  wallet: WithdrawalClient
  logger: FunLogger
  rail: WithdrawalRail
  context: WithdrawalLogContext
  /** Relay step progress (raw step action text), for optional host display. */
  onStepMessage?: (message: string) => void
}): Promise<Hex | undefined> {
  return new Promise<Hex | undefined>((resolve, reject) => {
    executeRelayQuote({
      logger,
      relayQuote,
      // A client that brings its own adapter already speaks relay's interface,
      // so it goes through unwrapped.
      walletClient: wallet.adaptedWallet ?? toRelayWallet(wallet, logger),
      onError: async (error) => {
        // Declining is recoverable — WARN, not an alertable ERROR.
        if (isUserRejectedError(error)) {
          logger.warn(`${rail}:executeRelayQuote:user_rejected`, context)
        } else {
          logger.error(`${rail}:executeRelayQuote:error`, error)
        }
        reject(error)
      },
      onProgress: (step) => {
        if (step?.action) {
          onStepMessage?.(step.action)
        }
      },
      onUserActionsCompleted: async (hash) => {
        logger.info(`${rail}:executeRelayQuote:userActionsCompleted`, {
          ...context,
          txHash: hash,
        })
        resolve(hash as Hex)
      },
    })
      // Relay fires its callbacks from inside this await, so once it settles
      // none is still coming — without these the promise could never settle.
      .then(() => resolve(undefined))
      .catch(reject)
  })
}
